import { DO_NOT_USE_OR_YOU_WILL_BE_FIRED_EXPERIMENTAL_CREATE_ROOT_CONTAINERS } from 'react-dom/client';
import { UserStruct, UserTypes, UserRoles, UserTypeInfo, UserRoleInfo } from '../api/auth';

import { messaging } from "../../src/helpers/firebase-config";
import { getToken, deleteToken, onMessage } from "firebase/messaging";
import { v4 as uuidv4 } from 'uuid';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { MemberRole, WpStatus } from '../redux/workplace/wpTypes';
import { setDraftPost } from '../redux/workplace/wpActions';
import { APIClient } from 'api/apiCore';
import * as urls from "api/urls";
import { AppDispatch } from 'redux/store';
import { Utils } from './utils';
import { WpModes } from '../constants';
import { WpPostInfoMem } from './wpPostInfoMem';
import { DbScope } from './dbScope';
import { CacheConfig } from './cacheConfig';


const DB_NAME = "chat.2s";
const DB_VERSION = 1;
const STORE_NAME = "store1";

class DbUtils {

  // One shared connection: opening IndexedDB is slow on native WebViews (Capacitor), and every call used to open a new one without closing it
  private static dbPromise: Promise<IDBDatabase> | null = null;

  private static async OpenDb(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          // No keyPath → you can provide keys manually
          db.createObjectStore(STORE_NAME);
        }
      };

      request.onsuccess = () => {
        const db = request.result;
        // drop the cached connection if the browser closes it or another tab needs to upgrade, so the next call reopens
        db.onclose = () => { DbUtils.dbPromise = null; };
        db.onversionchange = () => { db.close(); DbUtils.dbPromise = null; };
        resolve(db);
      };
      request.onerror = () => {
        DbUtils.dbPromise = null;
        reject(request.error);
      };
    });
    return this.dbPromise;
  }

  // iOS WKWebView can drop the connection while the app is backgrounded without firing onclose; db.transaction() then throws, so reopen once and retry
  private static async OpenTransaction(mode: IDBTransactionMode): Promise<IDBTransaction> {
    const db = await this.OpenDb();
    try {
      return db.transaction(STORE_NAME, mode);
    } catch (e) {
      this.dbPromise = null;
      const db2 = await this.OpenDb();
      return db2.transaction(STORE_NAME, mode);
    }
  }

  public static async PutDbData(key: IDBValidKey, data: any): Promise<number> {
    const transaction = await this.OpenTransaction("readwrite");
    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(STORE_NAME);
      // const request = store.add(data);
      // const request = store.add(data, key);
      const request = store.put(data, key); // insert or update

      request.onsuccess = () => resolve(request.result as number);
      request.onerror = () => reject(request.error);
    });
  }

  public static async GetDbData(key: IDBValidKey): Promise<any> {
    const transaction = await this.OpenTransaction("readonly");
    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  public static async GetDbDataByPrefix(prefix: string): Promise<any[]> {
    const transaction = await this.OpenTransaction("readonly");
    return new Promise((resolve, reject) => {
      const store = transaction.objectStore(STORE_NAME);

      // Create a key range to fetch items that start with "push."
      const keyRange = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
      const request = store.openCursor(keyRange);
      const results: any[] = [];

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          resolve(results);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  public static async DeleteDbDataByPrefix(prefix: string): Promise<void> {
    const transaction = await this.OpenTransaction("readwrite");
    return new Promise((resolve, reject) => {
      const objectStore = transaction.objectStore(STORE_NAME);

      // Open a cursor to iterate over all entries in the store
      const cursorRequest = objectStore.openCursor();

      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          // Check if the key starts with the specified prefix
          if (cursor.key.startsWith(prefix)) {
            cursor.delete(); // Delete the entry
          }
          cursor.continue(); // Move to the next entry
        }
      };

      cursorRequest.onerror = (error) => {
        console.error("Cursor error:", error);
        reject(error);
      };

      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = (error) => {
        console.error("Transaction error:", error);
        reject(error);
      };
    });
  }

  // 20261020: delete every entry whose key matches (one cursor pass)
  private static async DeleteDbDataWhere(match: (key: string) => boolean): Promise<void> {
    const transaction = await this.OpenTransaction("readwrite");
    return new Promise((resolve, reject) => {
      const cursorRequest = transaction.objectStore(STORE_NAME).openCursor();
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          if (match(String(cursor.key))) cursor.delete();
          cursor.continue();
        }
      };
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  // 20261020: cache entries belong to one user: key = `u{id}.` + key (see dbScope.ts). Device-level entries
  // ("push." = push history/settings) are not per user and are never touched here.
  private static isDeviceKey(key: string): boolean {
    return key.startsWith("push.");
  }

  private static scopedKey(key: string): string | null {
    DbUtils.cleanupLegacyKeysOnce();
    const prefix = DbScope.prefix();
    return prefix ? prefix + key : null; // no user -> no cache
  }

  // 20261020: delete one entry of the current user (no user -> nothing)
  public static async DeleteScoped(key: string): Promise<void> {
    const sKey = DbUtils.scopedKey(key);
    if (!sKey) return;
    try {
      const transaction = await this.OpenTransaction("readwrite");
      await new Promise<void>((resolve, reject) => {
        const request = transaction.objectStore(STORE_NAME).delete(sKey);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (e) {
      Utils.HandleException(e, false);
    }
  }

  // logout (keepPrefix null): drop every user's cache; user switch: keep the new user's (keepPrefix = `u{id}.`).
  // Device entries are always kept.
  public static async ClearUserCaches(keepPrefix: string | null = null): Promise<void> {
    try {
      DbUtils.topicIdxMem.clear(); // 20261022: in-memory mirror of the topic indexes being deleted
      await DbUtils.DeleteDbDataWhere(k => !DbUtils.isDeviceKey(k) && !(keepPrefix && k.startsWith(keepPrefix)));
    } catch (e) {
      Utils.HandleException(e, false);
    }
  }

  // keys written before 20261020 had no user prefix - remove them once per device
  private static legacyCleanupStarted = false;
  private static cleanupLegacyKeysOnce(): void {
    if (DbUtils.legacyCleanupStarted) return;
    DbUtils.legacyCleanupStarted = true;
    try {
      if (localStorage.getItem("dbScopedV1")) return;
      DbUtils.DeleteDbDataWhere(k => !DbUtils.isDeviceKey(k) && !/^u\d+\./.test(k))
        .then(() => localStorage.setItem("dbScopedV1", "1"))
        .catch(e => Utils.HandleException(e, false));
    } catch (e) {
      Utils.HandleException(e, false);
    }
  }

  public static async getOrSet(key: string, setData: any = null): Promise<any> {
    try {
      const sKey = DbUtils.scopedKey(key); // 20261020
      if (!sKey) return setData; // no user: nothing read, nothing saved
      if (setData) { // save data
        DbUtils.PutDbData(sKey, setData);
        return setData;
      } else {// get data
        return await DbUtils.GetDbData(sKey);
      }
    } catch (e) {
      Utils.HandleException(e, false);
      return setData;
    }
  }

  public static async wp_load_tag_for_filter(userId: number, setData: any = null): Promise<any> {
    const key = `wltff_${userId}`;
    return DbUtils.getOrSet(key, setData);
  }

  // the title list is written both whole (startup load) and read-modify-write (unread patch); chain every write so a patch that read an older list can't overwrite a newer save
  private static titleWriteChain: Promise<any> = Promise.resolve();
  private static queueTitleWrite(fn: () => Promise<any>): Promise<any> {
    const p = DbUtils.titleWriteChain.then(fn);
    DbUtils.titleWriteChain = p.catch(() => { });
    return p;
  }

  private static titleKey(wpMode: WpModes): string {
    return 'wp_load_post_title' + (wpMode == WpModes.Task ? "_t" : ""); // if workplace, then use old key because old key already in-used
  }

  public static async wp_load_post_title(wpMode: WpModes, setData: any = null): Promise<any> {
    // const key = DbUtils.titleKey(wpMode);
    const key = DbUtils.scopedKey(DbUtils.titleKey(wpMode)); // 20261020: per user
    if (!key) return setData;
    if (setData) {
      DbUtils.queueTitleWrite(() => DbUtils.PutDbData(key, setData)).catch(e => Utils.HandleException(e, false));
      return setData;
    }
    // return DbUtils.getOrSet(key, null);
    try {
      return await DbUtils.GetDbData(key); // key already scoped
    } catch (e) {
      Utils.HandleException(e, false);
      return null;
    }
  }

  // keep the cached title list's unread badge in step with redux, so the next startup paint doesn't show already-read topics as unread
  public static async wp_patch_title_unread(wpMode: WpModes, postIds: string[], unread: number): Promise<void> {
    // const key = DbUtils.titleKey(wpMode);
    const key = DbUtils.scopedKey(DbUtils.titleKey(wpMode)); // 20261020: per user
    if (!key) return;
    const idSet = new Set(postIds);
    return DbUtils.queueTitleWrite(async () => {
      const titles = await DbUtils.GetDbData(key);
      if (!Array.isArray(titles)) return;
      let changed = false;
      const next = titles.map((t: any) => {
        if (!idSet.has(t.postId) || t.unread === unread) return t;
        changed = true;
        return { ...t, unread };
      });
      if (changed) await DbUtils.PutDbData(key, next);
    }).catch(e => Utils.HandleException(e, false));
  }

  public static async wp_load_tag_for_toolbox(setData: any = null): Promise<any> {
    const key = `wltft`;
    return DbUtils.getOrSet(key, setData);
  }

  public static async tk_load_doctype_for_approval_center(setData: any = null): Promise<any> {
    const key = `tkdtac`;
    return DbUtils.getOrSet(key, setData);
  }

  public static async wp_load_post_conver(postId : string, setData: any = null): Promise<any> {
    const key = `wlpc_${postId}`;
    if (!setData) return DbUtils.getOrSet(key, null);
    DbUtils.wp_touch_topic(WpModes.Workplace, postId);
    await DbUtils.getOrSet(key, DbUtils.wp_cap_conver(setData));
    return setData;
  }

  public static async wp_load_task_conver(taskId : string, setData: any = null): Promise<any> {
    const key = `tkpc_${taskId}`;
    if (!setData) return DbUtils.getOrSet(key, null);
    DbUtils.wp_touch_topic(WpModes.Task, taskId);
    await DbUtils.getOrSet(key, DbUtils.wp_cap_conver(setData));
    return setData;
  }

  // 20261022: Workplace/Task conver cache limits (same idea as chatConverCache.ts).
  // Every save rewrites the whole topic entry, so after many "load more" a long thread made every save slower;
  // and topics were never dropped. Option 2 (one IndexedDB record per message, local-first like LINE) is pending.
  // Limits are per-device settings (cacheConfig.ts): wpMsgs = newest messages kept per topic (default 100),
  // wpTopics = most recently saved topics kept, WP + Task combined (default 200).

  // convers is oldest-first ("load more" prepends older pages) -> keep the tail
  private static wp_cap_conver(convers: any): any {
    const max = CacheConfig.get("wpMsgs");
    return Array.isArray(convers) && convers.length > max
      ? convers.slice(convers.length - max)
      : convers;
  }

  // one index for WP + Task together (like the chat cache: customer + supplier together)
  private static readonly TOPIC_IDX_KEY = "wptkidx";

  // every cache entry that belongs to one topic (dropped together, or the prefetch stamp would claim a conver that's gone)
  private static topicKeyPrefixes(wpMode: WpModes): string[] {
    return wpMode == WpModes.Task ? ["tkpc_", "tkpcs_", "tkpi_", "tkap_"] : ["wlpc_", "wlpcs_", "wlpi_"];
  }

  // m = "t" Task / "w" Workplace, p = postId, t = saved at (ms); newest first
  private static topicIdxMem = new Map<string, Array<{ m: string; p: string; t: number }>>(); // key = scoped index key
  private static topicIdxChain: Promise<any> = Promise.resolve();

  // topics cached now for the current user (Settings > cache); waits for pending index writes
  public static async wp_topic_counts(): Promise<{ wp: number; task: number }> {
    const sIdx = DbUtils.scopedKey(DbUtils.TOPIC_IDX_KEY);
    if (!sIdx) return { wp: 0, task: 0 };
    try {
      await DbUtils.topicIdxChain;
      const idx = DbUtils.topicIdxMem.get(sIdx) ?? await DbUtils.GetDbData(sIdx);
      const list: Array<{ m: string }> = Array.isArray(idx) ? idx : [];
      const task = list.filter(x => x.m === "t").length;
      return { wp: list.length - task, task };
    } catch (e) {
      Utils.HandleException(e, false);
      return { wp: 0, task: 0 };
    }
  }

  // mark a topic as just saved; drop the least recently saved topics past the wpTopics limit
  private static wp_touch_topic(wpMode: WpModes, postId: string): void {
    const sIdx = DbUtils.scopedKey(DbUtils.TOPIC_IDX_KEY);
    if (!sIdx || !postId) return;
    const m = wpMode == WpModes.Task ? "t" : "w";
    const userPrefix = DbScope.prefix()!;
    DbUtils.topicIdxChain = DbUtils.topicIdxChain.then(async () => {
      let idx = DbUtils.topicIdxMem.get(sIdx);
      if (!idx) {
        const stored = await DbUtils.GetDbData(sIdx);
        if (Array.isArray(stored)) {
          idx = stored;
        } else {
          // first run with the index: topics cached before it are untracked and would never be dropped - clear them once
          // (both modes; also the per-mode indexes "wpidx"/"tkidx" of the first dev build)
          idx = [];
          const prefixes = [...DbUtils.topicKeyPrefixes(WpModes.Workplace), ...DbUtils.topicKeyPrefixes(WpModes.Task)]
            .map(kp => userPrefix + kp);
          const keepKeys = new Set(DbUtils.topicKeyPrefixes(wpMode).map(kp => userPrefix + kp + postId));
          const oldIdx = new Set([userPrefix + "wpidx", userPrefix + "tkidx"]);
          await DbUtils.DeleteDbDataWhere(k => oldIdx.has(k) || (!keepKeys.has(k) && prefixes.some(p => k.startsWith(p))));
        }
      }
      if (idx[0]?.m === m && idx[0]?.p === postId) { // already the newest: nothing to write (saves fire on every conver change)
        DbUtils.topicIdxMem.set(sIdx, idx);
        return;
      }
      const next = [{ m, p: postId, t: Date.now() }, ...idx.filter(x => !(x.m === m && x.p === postId))];
      const maxTopics = CacheConfig.get("wpTopics");
      const keep = next.slice(0, maxTopics);
      const drop = next.slice(maxTopics);
      DbUtils.topicIdxMem.set(sIdx, keep);
      await DbUtils.PutDbData(sIdx, keep);
      for (const d of drop) {
        const dMode = d.m === "t" ? WpModes.Task : WpModes.Workplace;
        WpPostInfoMem.remove(dMode, d.p);
        await Promise.all(DbUtils.topicKeyPrefixes(dMode).map(kp => DbUtils.DeleteScoped(kp + d.p)));
      }
    }).catch(e => Utils.HandleException(e, false));
  }

  // value is always an object ({lcd, at}), never null — lcd = the title's lastChatDate the cached conver was pre-fetched for, at = fetch time (ms)
  public static async wp_load_conver_stamp(wpMode: WpModes, postId: string, setData: any = null): Promise<any> {
    const key = `${wpMode == WpModes.Task ? "tkpcs" : "wlpcs"}_${postId}`;
    return DbUtils.getOrSet(key, setData);
  }

  // value is always an object ({approveStatus, approvalFlow}), never null — getOrSet treats a null setData as a read
  public static async wp_load_task_approval(taskId : string, setData: any = null): Promise<any> {
    const key = `tkap_${taskId}`;
    return DbUtils.getOrSet(key, setData);
  }

  public static async wp_load_post_info(postId : string, setData: any = null): Promise<any> {
    const key = `wlpi_${postId}`;
    const rs = await DbUtils.getOrSet(key, setData);
    WpPostInfoMem.set(WpModes.Workplace, postId, rs);
    return rs;
  }

  public static async wp_load_task_info(taskId : string, setData: any = null): Promise<any> {
    const key = `tkpi_${taskId}`;
    const rs = await DbUtils.getOrSet(key, setData);
    WpPostInfoMem.set(WpModes.Task, taskId, rs);
    return rs;
  }

  // 20261021: server refused to open the task (TASK_NO_PERMISSION) - drop everything cached for it
  public static async wp_forget_task(taskId: string): Promise<void> {
    WpPostInfoMem.remove(WpModes.Task, taskId);
    await Promise.all([`tkpi_${taskId}`, `tkpc_${taskId}`, `tkpcs_${taskId}`, `tkap_${taskId}`].map(k => DbUtils.DeleteScoped(k)));
  }

  public static async wp_save_post_info(postInfo: any): Promise<any> {
    if (!postInfo?.postId) return;
    return postInfo.wpMode == WpModes.Task
      ? DbUtils.wp_load_task_info(postInfo.postId, postInfo)
      : DbUtils.wp_load_post_info(postInfo.postId, postInfo);
  }




}
DbScope.onClear((keepPrefix) => DbUtils.ClearUserCaches(keepPrefix)); // 20261020: logout / user switch

export { DbUtils };